import { DataGrid, GridColDef, GridToolbarContainer } from '@mui/x-data-grid';
import { roleContext } from '../../context/RoleContext';
import * as React from 'react';
import { BusinessHours } from '../../types/shop';
import Box from '@mui/material/Box';
import { useContext } from 'react';
import { Grid, MenuItem, Select, Switch } from '@mui/material';
import { Alert } from '@mui/material';
import Button from '@mui/material/Button';
import {
  addBusinessHoursToBarber,
  addBusinessHoursToShopBarber,
} from '../../middleware/registration_service/updateRequest';
import { UserRole } from '../../types/role';
import { hours as defaultHours } from '../../defaults/BusinessHours';
import { useProfileData } from '../../context/ProfileDataContext';

// ── Time options: 00:00 – 23:30 in 30-min steps ──────────────────────────────
const TIME_OPTIONS: string[] = [];
for (let h = 0; h < 24; h++) {
  TIME_OPTIONS.push(`${String(h).padStart(2, '0')}:00`);
  TIME_OPTIONS.push(`${String(h).padStart(2, '0')}:30`);
}

const formatTime = (time: string | undefined): string => {
  if (!time) return '09:00';
  const timeFormatRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
  if (timeFormatRegex.test(time)) return time;
  try {
    return new Date(`1970-01-01T${time}`).toTimeString().slice(0, 5);
  } catch {
    return '09:00';
  }
};

// ── Shared context so CustomToolbar can read isDirty + trigger save ───────────
interface BHCtxValue {
  isDirty: boolean;
  saving: boolean;
  updated: boolean;
  onSave: () => Promise<void>;
}
const BHCtx = React.createContext<BHCtxValue>({
  isDirty: false,
  saving: false,
  updated: false,
  onSave: async () => {},
});

// ── Toolbar ───────────────────────────────────────────────────────────────────
const CustomToolbar = () => {
  const { isDirty, saving, updated, onSave } = useContext(BHCtx);
  return (
    <GridToolbarContainer
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        mr: { xs: '5px', sm: '10px' },
      }}
    >
      {updated && (
        <Alert severity="success" sx={{ mr: 1 }}>
          Business hours updated successfully
        </Alert>
      )}
      <Button
        variant="contained"
        disabled={!isDirty || saving}
        onClick={onSave}
        sx={{
          mt: '1em',
          ml: '5px',
          bgcolor: '#dd8709',
          color: '#fff',
          p: 1.5,
          fontFamily: 'GFS Didot',
          fontWeight: 'bold',
          '&:hover': { bgcolor: '#b86e08' },
          '&.Mui-disabled': { bgcolor: '#e0b96a', color: '#fff' },
        }}
      >
        {saving ? 'Saving…' : 'Update Hours'}
      </Button>
    </GridToolbarContainer>
  );
};

const label = { inputProps: { 'aria-label': 'Switch demo' } };

// ── Main component ────────────────────────────────────────────────────────────
export default function BusinessHoursDisplay() {
  const role = useContext(roleContext);
  const { barberId, barber, shopBarber, setBarber, setShopBarber } =
    useProfileData();
  const contextBusinessHours =
    role === UserRole.SHOP_BARBER
      ? shopBarber?.businessHours
      : role === UserRole.BARBER
      ? barber?.businessHours
      : undefined;
  const [businessHour, setBusinessHour] = React.useState<BusinessHours>(
    contextBusinessHours ?? defaultHours
  );
  const [isDirty, setIsDirty] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [updated, setUpdated] = React.useState(false);

  // ── Helpers ──
  const updateTime = (
    day: string,
    field: 'startTime' | 'endTime',
    value: string
  ) => {
    setBusinessHour((prev) => ({
      ...prev,
      businessHoursMap: {
        ...prev.businessHoursMap,
        [day]: {
          ...prev.businessHoursMap[day as keyof typeof prev.businessHoursMap],
          [field]: value,
        },
      },
    }));
    setIsDirty(true);
    setUpdated(false);
  };

  const handleToggle = (day: string, checked: boolean) => {
    setBusinessHour((prev) => ({
      ...prev,
      businessHoursMap: {
        ...prev.businessHoursMap,
        [day]: {
          ...prev.businessHoursMap[day as keyof typeof prev.businessHoursMap],
          open: checked,
        },
      },
    }));
    setIsDirty(true);
    setUpdated(false);
  };

  const handleSave = async () => {
    const payload: BusinessHours = { ...businessHour, partnerId: barberId };
    setSaving(true);
    try {
      let response;
      if (role === UserRole.SHOP_BARBER) {
        response = await addBusinessHoursToShopBarber(payload, barberId);
        if (response.status === 200) {
          setShopBarber((prev) =>
            prev ? { ...prev, businessHours: payload } : prev
          );
        }
      } else if (role === UserRole.BARBER) {
        response = await addBusinessHoursToBarber(payload, barberId);
        if (response.status === 200) {
          setBarber((prev) =>
            prev ? { ...prev, businessHours: payload } : prev
          );
        }
      } else {
        console.error('Unexpected role:', role);
        return;
      }
      if (response.status === 200) {
        setIsDirty(false);
        setUpdated(true);
      }
    } catch (err) {
      console.error('Error updating business hours:', err);
    } finally {
      setSaving(false);
    }
  };

  // ── Columns ──
  const columns: GridColDef[] = [
    {
      field: 'day',
      headerName: 'Day',
      width: 200,
      sortable: false,
      hideSortIcons: true,
      headerClassName: 'custom-header',
      cellClassName: 'custom-cell',
    },
    {
      field: 'startTime',
      headerName: 'Open',
      width: 220,
      sortable: false,
      hideSortIcons: true,
      renderCell: (params) => (
        <Select
          size="small"
          value={params.row.startTime}
          onChange={(e) =>
            updateTime(params.row.day, 'startTime', e.target.value as string)
          }
          sx={{ width: '100%' }}
        >
          {TIME_OPTIONS.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      field: 'endTime',
      headerName: 'Close',
      width: 220,
      sortable: false,
      hideSortIcons: true,
      renderCell: (params) => (
        <Select
          size="small"
          value={params.row.endTime}
          onChange={(e) =>
            updateTime(params.row.day, 'endTime', e.target.value as string)
          }
          sx={{ width: '100%' }}
        >
          {TIME_OPTIONS.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      field: 'open',
      headerName: 'Closed / Open',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Switch
          {...label}
          checked={params.row.open}
          onChange={(e) => handleToggle(params.row.day, e.target.checked)}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: '#dd8709' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: '#dd8709',
            },
          }}
        />
      ),
    },
  ];

  // ── Rows ──
  const DAYS = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ] as const;
  const dataRow = DAYS.map((day, i) => ({
    id: i + 1,
    day,
    startTime: formatTime(businessHour?.businessHoursMap[day]?.startTime),
    endTime: formatTime(businessHour?.businessHoursMap[day]?.endTime),
    open: businessHour?.businessHoursMap[day]?.open ?? true,
  }));

  // ── Sync local editable copy when context data loads/changes ──
  React.useEffect(() => {
    if (contextBusinessHours) setBusinessHour(contextBusinessHours);
  }, [contextBusinessHours]);

  return (
    <BHCtx.Provider value={{ isDirty, saving, updated, onSave: handleSave }}>
      <Grid container spacing={2}>
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={dataRow}
            columns={columns}
            slots={{ toolbar: CustomToolbar }}
            pageSizeOptions={[]}
            disableColumnMenu
            disableRowSelectionOnClick
            hideFooterPagination
            sortingOrder={[null]}
            getRowHeight={() => 60}
          />
        </Box>
      </Grid>
    </BHCtx.Provider>
  );
}
